// Customer consultation API. The server's HttpOnly __Host- cookie is the
// authoritative credential. localStorage remains temporarily only to bootstrap
// already-open pre-migration tabs through the server's legacy body fallback.
const LS_KEY = 'fttx_consult_session_v1';

function getToken() {
  try { return localStorage.getItem(LS_KEY) || null; } catch { return null; }
}
function setToken(token) {
  try { localStorage.setItem(LS_KEY, token); } catch { /* private mode */ }
}

async function postJson(path, body) {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let detail = '';
    try { const j = await res.json(); detail = j.error || j.detail || j.message || ''; } catch {}
    throw new Error(detail || `خطای سرور (${res.status})`);
  }
  return res.json();
}

export const consultApi = {
  getToken, setToken,
  // Resolve/create the cookie session. The body token is migration-only.
  startSession: async () => {
    const data = await postJson('/api/consult/session', { session_token: getToken() });
    setToken(data.session_token);
    return data;
  },
  getMessages: async () => {
    const res = await fetch('/api/consult/messages', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('خواندن گفتگو ناموفق بود.');
    return res.json();
  },
  sendMessage: (content, location) =>
    postJson('/api/consult/message', { session_token: getToken(), content, location }),
  getCoverageOffers: ({ lat, lng }) =>
    postJson('/api/consult/serviceability/offers', { lat, lng, service_type: 'fiber' }),
  endConsultation: (outcome) =>
    postJson('/api/consult/end', { session_token: getToken(), outcome }),

  // ---- Phase 3: realtime voice -------------------------------------------

  // Phase 1 of WebRTC handshake - mint ephemeral + persistent session_id.
  // Returns: { ephemeral_token, session_id, expires_at }
  startRealtimeCall: (location) =>
    postJson('/api/consult/realtime/token', { session_token: getToken(), location }),

  // Phase 2 of WebRTC handshake - report the rtc_call_id learned from the
  // SDP exchange's Location header so the backend can open the sideband WS.
  // Returns: { ok: true }
  attachRealtimeCall: (sessionId, rtcCallId) =>
    postJson('/api/consult/realtime/attach', {
      session_token: getToken(),
      session_id: sessionId,
      rtc_call_id: rtcCallId,
    }),

  // GPT-Live (transport 'webrtc-live'): the server exchanges the SDP offer
  // with OpenAI. Returns: { session_id, sdp }
  createLiveCall: (sessionId, sdp) =>
    postJson('/api/consult/realtime/live', { session_token: getToken(), session_id: sessionId, sdp }),

  // GPT-Live location update (its data channel is receive-only).
  // Returns: { ok: boolean }
  shareLiveLocation: (location) =>
    postJson('/api/consult/realtime/location', { session_token: getToken(), location }),

  // Explicit end-call.
  // Returns: { ok: true }
  endRealtimeCall: () =>
    postJson('/api/consult/realtime/end', { session_token: getToken() }),

  // Poll for a registration link the agent may have offered during a voice
  // call (voice path has no server->browser socket). Returns the URL string
  // or null; fails open (returns null) on any network/server error.
  getRegistrationLink: async () => {
    try {
      const res = await fetch('/api/consult/registration-link', {
        credentials: 'same-origin',
      });
      if (!res.ok) return null;
      const data = await res.json();
      return data.url || null;
    } catch {
      return null;
    }
  },

  // Read-only resync probe - fails open (assume no call) on any non-2xx.
  // Returns: { active: boolean, session_id: string|null }
  getRealtimeStatus: async () => {
    try {
      const res = await fetch('/api/consult/realtime/status', {
        credentials: 'same-origin',
      });
      if (!res.ok) return { active: false, session_id: null };
      return res.json();
    } catch {
      return { active: false, session_id: null };
    }
  },
};
